export default class EventsTracking {
  /**
   * This class aims to remove code duplication among components by moving most of the tracking
   * logic in one place and exposing generic methods that these components may consume in whatever
   * manner they need.
   * @param {String} taggingId String id used to identify a component and its tracking config
   * @param {Object} componentInstance Execution scope of the component
   * @returns {void}
   */
  constructor(taggingId, componentInstance) {
    const brandLevelTaggingConfig = document.querySelector(
      `[data-brand-level-tagging-id="${taggingId}"]`,
    );
    const instanceTaggingConfig = document.getElementById(taggingId);
    if (!instanceTaggingConfig) return;

    const taggingConfiguration = this.tryParseJson(instanceTaggingConfig.textContent.trim());
    let brandLevelTaggingConfiguration = {};
    if (brandLevelTaggingConfig) {
      brandLevelTaggingConfiguration = this.tryParseJson(
        brandLevelTaggingConfig.textContent.trim(),
      );
    }

    const trackedEvents = Object.keys(taggingConfiguration);

    if (!taggingConfiguration || trackedEvents.length === 0) return;

    const taggableComponent = document.querySelector(`[data-refapp-tagging-id="${taggingId}"]`);
    trackedEvents.forEach((event) =>
      this.trackerInit(
        taggableComponent,
        taggingConfiguration[event],
        brandLevelTaggingConfiguration[event],
        event,
        componentInstance[`${event}TrackerOptions`],
      ),
    );
  }

  /**
   * The tracker init function assigns event listeners according to the configuration supplied
   * @param {HTMLElement} taggableComponent Element to which the listener is to be added
   * @param {Object} trackerConfig Configuration object for this particular event
   * @param {Object} brandLevelConfig Addon configuration object for the brand level will be merged
   * @param {String} eventName name of the HTML event to attach a listener to
   * @param {Object} handlerOptions HTML event listener options, default values use capture phase
   *                                and the listener is only executed once
   * @returns {void}
   */
  trackerInit(
    taggableComponent,
    trackerConfig,
    brandLevelConfig,
    eventName,
    handlerOptions = { capture: true, once: true },
  ) {
    if (!taggableComponent) return;

    // monitor slider movement events, whichever the user interaction source
    taggableComponent.addEventListener(
      eventName,
      this.handleEvent.bind(this, eventName, trackerConfig, brandLevelConfig),
      handlerOptions,
    );
  }

  /**
   * Event handler passed to each of the tracked events
   * @param {String} trackedEvent Name of the tracked event
   * @param {Array} trackerConfig Array of same-type events
   * @param {Array=} brandLevelConfig Array of brand-level properties to merge in the final event
   * @param {Event} e Event instance received by the handler function
   * @returns {void}
   */
  handleEvent(trackedEvent, trackerConfig, brandLevelConfig = [], e) {
    const element = e.target.closest('[data-refapp-tagging-target]');
    if (!element) return;

    const taggingTarget = element.dataset?.refappTaggingTarget;

    if (!taggingTarget) return;
    const event = trackerConfig.find((c) => c.target === taggingTarget);
    const extras = this.tryParseJson(e.target.dataset?.refappTaggingExtras);
    const brandEvent = brandLevelConfig.find((c) => c.target === taggingTarget);

    if (!event) return;

    window.dataLayer = [
      ...(window.dataLayer || []),
      {
        ...event.payload,
        ...extras,
        ...(brandEvent ? brandEvent.payload : {}),
      },
    ];
  }

  /**
   * Utility function that attempts to parse a JSON string into a JS object
   * @param {String} jsonString String to attempt to parse into a json object
   * @returns {Object}
   */
  tryParseJson = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      return {};
    }
  };
}
